/**
 * Qaror QR-kodi ochiladigan ochiq sahifa (`/tekshiruv/qaror/:id`).
 *
 * Kirish talab qilinmaydi: qaror haqiqiyligi (tashkilot, lavozim, natija, sana, ERI sertifikati
 * va yaxlitlik belgisi) ko'rsatiladi. Shaxsiy ma'lumot — F.I.Sh., PINFL — bu yerda chiqmaydi.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { leaseApi } from "@/shared/api/endpoints";
import type { LeaseDecisionVerification } from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";
import { PublicLayout } from "@/shared/layouts/PublicLayout";
import { formatDateTime } from "@/shared/lib/format";
import { KeyValues } from "@/shared/ui/Drawer";
import { Icon } from "@/shared/ui/Icon";

export function DecisionVerifyPage() {
  const { id = "" } = useParams();
  const { locale } = useI18n();
  const [result, setResult] = useState<LeaseDecisionVerification | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">("loading");

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    leaseApi
      .verifyDecision(id)
      .then((data) => {
        if (cancelled) return;
        setResult(data);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("missing");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <PublicLayout>
      <section className="section">
        <div className="container cms-page">
          <h1>Qaror haqiqiyligini tekshirish</h1>
          {state === "loading" ? <p className="muted">Tekshirilmoqda…</p> : null}
          {state === "missing" ? (
            <div className="panel-state is-error" role="status">
              <Icon name="alert" size={18} />
              <p>
                <b>Qaror topilmadi</b>
                <span>Bu QR-kod tizimda qayd etilgan qarorga tegishli emas. Hujjat haqiqiy bo'lmasligi mumkin.</span>
              </p>
            </div>
          ) : null}
          {state === "ready" && result?.valid ? (
            <>
              <p className="alert alert-success" role="status">
                <Icon name="checkCircle" size={18} />
                <span>
                  <b>Qaror tizimda qayd etilgan.</b> Quyidagi ma'lumotlar rasmiy yozuvga mos kelishi kerak.
                </span>
              </p>
              <KeyValues
                rows={[
                  { label: "Ariza raqami", value: result.application_number ?? "—" },
                  { label: "Bosqich", value: result.stage ?? "—" },
                  { label: "Natija", value: result.outcome ?? "—" },
                  { label: "Tashkilot", value: result.organization || "—" },
                  { label: "Lavozim", value: result.position || "—" },
                  { label: "Sana va vaqt", value: result.decided_at ? formatDateTime(result.decided_at, locale) : "—" },
                  { label: "ERI sertifikat raqami", value: result.eri_serial || "—" },
                  { label: "Yaxlitlik belgisi (SHA-256)", value: <code style={{ wordBreak: "break-all" }}>{result.hash}</code> },
                ]}
              />
            </>
          ) : null}
          <p>
            <Link className="btn btn-ghost btn-sm" to="/">
              Bosh sahifa
            </Link>
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
